import React from 'react'

/**
 * Formata observações convertendo "=== TÍTULO ===" em elementos React com estilo de título
 * @param observations - Texto das observações
 * @returns Array de elementos React formatados
 */
export function formatObservations(observations: string): React.ReactNode[] {
  if (!observations) return []
  
  // Se observations é apenas whitespace, retornar array vazio
  if (observations.trim() === '') return []

  // Dividir por linhas
  const lines = observations.split('\n')
  const formatted: React.ReactNode[] = []
  let currentParagraph: string[] = []

  lines.forEach((line, index) => {
    const trimmedLine = line.trim()
    
    // Ignorar linhas que são apenas whitespace
    if (trimmedLine === '') {
      // Linha vazia ou apenas espaços - finalizar parágrafo atual se houver conteúdo não vazio
      if (currentParagraph.length > 0) {
        const paragraphText = currentParagraph.join('\n')
        // Só adicionar se o parágrafo tiver conteúdo real (não apenas espaços ou newlines)
        const trimmedParagraph = paragraphText.trim()
        // Verificar se há conteúdo real (não apenas whitespace, tabs, newlines)
        // Se trimmedParagraph está vazio OU se após remover todos os whitespace ainda está vazio, não adicionar
        const nonWhitespaceContent = trimmedParagraph.replace(/\s+/g, '')
        // Só adicionar se houver conteúdo não-whitespace
        // Note: nonWhitespaceContent should always be non-empty since currentParagraph only contains lines with non-empty trimmed content
        if (nonWhitespaceContent) {
          formatted.push(
            <p key={`para-${index}`} className="mb-3 text-sm text-secondary-700">
              {trimmedParagraph}
            </p>
          )
        }
        currentParagraph = []
      }
      // Ignorar linhas vazias ou com apenas espaços - não adicionar ao parágrafo
      return
    }
    
    // Detectar títulos com "==="
    const titleMatch = line.match(/^=== (.+?) ===$/)
    
    if (titleMatch) {
      // Se há parágrafo acumulado, adicionar antes do título
      if (currentParagraph.length > 0) {
        const paragraphText = currentParagraph.join('\n')
        // Só adicionar se o parágrafo tiver conteúdo real (não apenas espaços ou newlines)
        const trimmedParagraph = paragraphText.trim()
        // Verificar se há conteúdo real (não apenas whitespace, tabs, newlines)
        // Se trimmedParagraph está vazio OU se após remover todos os whitespace ainda está vazio, não adicionar
        const nonWhitespaceContent = trimmedParagraph.replace(/\s+/g, '')
        // Só adicionar se houver conteúdo não-whitespace
        // Note: nonWhitespaceContent should always be non-empty since currentParagraph only contains lines with non-empty trimmed content
        if (nonWhitespaceContent) {
          formatted.push(
            <p key={`para-${index}`} className="mb-3 text-sm text-secondary-700">
              {trimmedParagraph}
            </p>
          )
        }
        currentParagraph = []
      }
      
      // Adicionar título formatado como semi-título bonito
      // Only render heading if it has content (accessibility requirement)
      const titleText = titleMatch[1]?.trim()
      if (titleText) {
        formatted.push(
          <h4 key={`title-${index}`} className="mt-4 mb-2.5 text-xs font-bold text-secondary-800 uppercase tracking-[0.15em] text-secondary-600">
            {titleText}
          </h4>
        )
      }
    } else {
      // Linha com conteúdo real (não é whitespace) - adicionar ao parágrafo atual
      // Já verificamos que trimmedLine !== '' acima, então podemos adicionar
      currentParagraph.push(line)
    }
  })

  // Adicionar último parágrafo se houver conteúdo não vazio
  if (currentParagraph.length > 0) {
    const paragraphText = currentParagraph.join('\n')
    // Só adicionar se o parágrafo tiver conteúdo real (não apenas espaços ou newlines)
    // Double-check that the entire paragraph text is not just whitespace
    const trimmedParagraph = paragraphText.trim()
    // Verificar se há conteúdo real (não apenas whitespace, tabs, newlines)
    // Se trimmedParagraph está vazio OU se após remover todos os whitespace ainda está vazio, não adicionar
    const nonWhitespaceContent = trimmedParagraph.replace(/\s+/g, '')
    // Só adicionar se houver conteúdo não-whitespace
    // Note: nonWhitespaceContent should always be non-empty since currentParagraph only contains lines with non-empty trimmed content
    if (nonWhitespaceContent) {
      formatted.push(
        <p key={`para-final`} className="mb-3 text-sm text-secondary-700">
          {trimmedParagraph}
        </p>
      )
    }
  }

  // Final check: se após processar todas as linhas não há conteúdo formatado,
  // retornar array vazio (pode acontecer se todas as linhas eram apenas whitespace)
  return formatted
}

/**
 * Componente React para renderizar observações formatadas
 */
export function FormattedObservations({ observations }: { observations?: string | null }) {
  if (!observations) return null
  
  // Early return for whitespace-only strings
  const trimmed = observations.trim()
  if (trimmed === '') return null

  const formatted = formatObservations(observations)
  
  // Se não há conteúdo formatado, retornar null
  if (formatted.length === 0) {
    return null
  }
  
  // Return formatted content (else branch of line 123)
  return <div className="text-sm text-secondary-700">{formatted}</div>
}

